import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import useProviderActionModal from 'components/widgets/ProviderActionModal/useProviderActionModal'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { setupNetwork as networkSetup, SetupNetworkResponse } from 'utils/wallet'
import { getChainIdNumber } from 'utils/getChainIdNumber'
import { getChainNameByChainId } from '@mprolab/sdk'
import useToast from './useToast'

/** How long a wallet is given to land on the requested chain before we say so. */
export const NETWORK_SWITCH_TIMEOUT_MS = 45_000

export const useSetupNetwork = () => {
  const { provider } = useWeb3React()
  const { t } = useTranslation()
  const { onPresentProviderActionModal, onDismissProviderActionModal } = useProviderActionModal({
    manual: false
  })
  const { toastSuccess } = useToast()

  /**
   * Waits for the wallet to actually be on `chainId` — bounded.
   *
   * It used to loop `while (!isUpdated)` with no end: a wallet that never
   * switched left this promise polling every second for as long as the tab
   * lived, and a provider that threw inside the loop turned into an unhandled
   * rejection with nothing above it to catch. Callers get `false` after the
   * timeout and say something, which is the whole point.
   */
  const awaitForNetworkSetup = useCallback(
    async (chainId: number, timeoutMs = NETWORK_SWITCH_TIMEOUT_MS) => {
      const deadline = Date.now() + timeoutMs
      while (Date.now() < deadline) {
        try {
          const providerChainId = await provider?.provider?.request({
            method: 'eth_chainId',
            params: []
          })
          if (getChainIdNumber(providerChainId) === chainId) return true
        } catch {
          // A wallet that will not answer eth_chainId is a wallet that has not
          // switched. Keep waiting until the deadline rather than throwing out
          // of a loop nobody is catching.
        }
        await new Promise((resolve) => setTimeout(resolve, 1000))
      }
      return false
    },
    [provider?.provider]
  )

  const setupNetwork = useCallback(
    async ({ chainId, modal = true, error = true }: { chainId: number; modal?: boolean; error?: boolean }) => {
      if (!provider) {
        if (error) throw new Error('No provider')
        return false
      }

      const currentChainId = await provider?.provider.request({
        method: 'eth_chainId',
        params: []
      })
      if (parseInt(currentChainId, 16) === chainId || currentChainId === chainId) return true
      try {
        if (modal) onPresentProviderActionModal(parseInt(currentChainId, 16), chainId)
        const chainName = getChainNameByChainId(chainId)
        const res = await networkSetup(chainId, provider?.provider)
        let isSwitched = false
        switch (res) {
          case SetupNetworkResponse.REQUEST_SUCCESS:
            if (modal) onDismissProviderActionModal()
            toastSuccess(`${t('walletConnect.connectSuccess')} to ${chainName}`)
            isSwitched = true
            break
          case SetupNetworkResponse.REQUEST_REJECTED:
          case SetupNetworkResponse.REQUEST_FAILED:
            if (modal) onDismissProviderActionModal()
            if (error) throw new Error(t('globalErrors.requestRejected'))
            break
          case SetupNetworkResponse.METHOD_NOT_FOUND:
            if (modal) onDismissProviderActionModal()
            if (error) throw new Error(t('globalErrors.methodNotFound'))
            break
          case SetupNetworkResponse.REQUEST_PENDING:
            if (error) throw new Error(t('globalErrors.switchNetworkPending'))
            break
          default:
            if (error) throw new Error(t('globalErrors.failedToSwitchNetwork'))
            break
        }
        return isSwitched
      } catch (caughtError: any) {
        if (error) throw new Error(caughtError?.message ?? 'Failed to switch network')
        return false
      }
    },
    [provider, provider?.provider]
  )

  return { setupNetwork, awaitForNetworkSetup }
}
